import React from 'react';
import Select from './FilterSelect';
import PropTypes from 'prop-types';
import axios from '../axios';
import locale from '../locale';
import { uniqBy, pickBy, some, filter, find, includes } from 'lodash';
import getCategoryNameForUrl from '../Models/GetCategoryForUrl';

/**
 * Prepends an empty "all" entry to the given list of entries.
 * 
 * @param {array} array 
 */
function merge(array) {
    return [{ value: '-', label: locale.all }].concat(array);
};

class Filters extends React.Component {

    constructor (props, context) {
        super(props, context);

        this.categoryRef = React.createRef();
        this.yearRef = React.createRef();
        this.distinctionRef = React.createRef();

        this.state = {
            filters: {}, // List of filters as received from API
            options: {}, // List of options to display in selects
            values: this.props.values   // User-selected options
        };
    }

    componentDidUpdate (prevProps, prevState) {
        if (this.props.values !== prevProps.values) {
            this.setState({ values: this.props.values });

            if (this.props.values.text != prevProps.values.text) {
                this.onChange('text', { target: { value: this.props.values.text } });
            }
        }
    }

    componentWillMount () {
        if (window.ICONIC_PRE_FILTERS) {
            let filters = window.ICONIC_PRE_FILTERS;
            this.setState({ filters }, () => {
                this.updateFilterOptions(filters);
            });
            return;
        }
        
        this.loadFilterOptions();
    }

    /**
     * Requests filter options from the API.
     */
    async loadFilterOptions () {
        let response = await axios.get('filters');
        let filters = response.data;

        this.setState({ filters }, () => {
            this.updateFilterOptions(filters);
        });
    }

    /**
     * Generates a list of select options for every filter and stores them. 
     * 
     * @param {object} filters
     */
    updateFilterOptions (filters) {
        let mapToOption = (object) => {
            return Object.keys(object).map(alias => ({ value: alias, label: object[alias][iconicLocale] }));
        };

        // We want unique categories, even if aliases differ.
        let categories = this.createCategoryOptions(filters.categories);
        let tags = filters.tags[iconicLocale].map(tag => ({ value: tag, label: tag }));

        let options = {
            category: categories,
            tag: merge(tags),
            award: merge(mapToOption(filters.awards)),
            year: merge(filters.years.map(year => ({ value: year, label: year }))),
            distinction: this.createDistinctionOptions(),
        };

        this.setState({ options });
    }

    /**
     * Creates select options for categories.
     * 
     * @param {object} categories
     * @return {array}
     */
    createCategoryOptions (categories) {
        categories = merge(Object.keys(categories)
            .map(alias => ({ value: categories[alias][iconicLocale], label: categories[alias][iconicLocale] })));

        return uniqBy(categories, 'label');
    }

    createDistinctionOptions (award) {
        let distinctions = this.state.filters.distinctions;

        let list = [];
        for (let alias of Object.keys(distinctions)) {
            let distinction = distinctions[alias];
            if (award && !includes(distinction.awards, award)) continue;

            list.push({ value: alias, label: distinction.name });
        }
        
        return merge(list);
    }

    /**
     * Updates the state and calls the prop onChange once a filter
     * was changed.
     * 
     * @param {string} filter The filter that was changed
     * @param {*} selected The selected value of the filter
     */
    onChange (filter, selected) {
        let filters = this.state.values;
        filters[filter] = selected ? (selected.value || selected.target.value) : null;

        if (filter === 'award') {
            this.onAwardChange(filters[filter]);
        }

        this.setState({ values: filters });
        this.props.onChange(filters);
    }

    /**
     * Makes sure that only valid year and category options for the newly
     * selected filter remain and removes the rest.
     * 
     * @param {string} value The new value of the award filter
     */
    onAwardChange (value) {
        if (value === '-') value = null;

        let options = this.state.options;
        let values = this.state.values;
        let categories = this.state.filters.categories;
        let years = this.state.filters.years;

        if (value) {
            // 1. Set Category filter
            categories = pickBy(categories, f => !f.awards || f.awards.includes(value));
            let selected = values.category;
            let stillValid = some(categories, category => category[iconicLocale] === selected);

            if (!stillValid) {
                values.category = '-';
                this.setState({ values });
                if (this.categoryRef.current && this.categoryRef.current.select.current.select)
                    this.categoryRef.current.select.current.select.setValue({ value: values.category, label: locale.all });
            }
            
            // 2. Set Year filter
            years = filter(years, year => {
                year = parseInt(year);
                if (value === 'iaim') return year >= 2018;
                if (value === 'iaii') return year >= 2016;
                return true;
            });
            selected = values.year;
            stillValid = some(years, year => year == selected);

            if (!stillValid) {
                values.year = '-';
                this.setState({ values });
                if (this.yearRef.current && this.yearRef.current.select.current.select)
                    this.yearRef.current.select.current.select.setValue({ value: values.year, label: locale.all });
            }

            // 3. Set Distinction filter
            options.distinction = this.createDistinctionOptions(value);
            selected = values.distinction;
            stillValid = some(options.distinction, entry => entry.value == selected)

            if (!stillValid) {
                values.distinction = '-';
                this.setState({ values });
                if (this.distinctionRef.current && this.distinctionRef.current.select.current.select)
                    this.distinctionRef.current.select.current.select.setValue({ value: values.distinction, label: locale.all });
            }
        }

        options.category = this.createCategoryOptions(categories);
        options.year = merge(years.map(year => ({ value: year, label: year })));
        this.setState({ options });        
    }

    generateValue (name) {
        let selected = this.state.values[name];
        let options = this.state.options[name];

        if (!selected) return merge([])[0];
        
        let value = find(options, o => o.value == selected);
        if (name === 'category') {
            value = find(options, o => getCategoryNameForUrl(o.value) == getCategoryNameForUrl(selected));
        }

        if (!value) return merge([])[0];

        return value;
    }

    render () {
        let options = this.state.options;

        return (
            <div className="directory-filter">
                <div className="text">
                    <input 
                        placeholder={ locale.search }
                        type="text"
                        onChange={ this.onChange.bind(this, 'text') }
                        value={ this.state.values.text || '' }
                    />
                </div>

                <div className="selects">
                    <Select
                        type="award"
                        value={ this.generateValue('award') }
                        options={ options.award }
                        onChange={ this.onChange.bind(this, 'award') }
                    />

                    <Select
                        type="year"
                        value={ this.generateValue('year') }
                        options={ options.year }
                        onChange={ this.onChange.bind(this, 'year') }
                        ref={ this.yearRef }
                    />

                    <Select
                        type="category"
                        value={ this.generateValue('category') }
                        options={ options.category }
                        onChange={ this.onChange.bind(this, 'category') }
                        ref={ this.categoryRef }
                    />

                    <Select
                        type="distinction"
                        value={ this.generateValue('distinction') }
                        options={ options.distinction }
                        onChange={ this.onChange.bind(this, 'distinction') }
                        ref={ this.distinctionRef }
                    />
                </div>
            </div>
        );

        /*
        // TODO: Add the select for tags back in once tags are re-added
        <Select
            type="tags"
            value={ this.generateValue('tag') }
            options={ options.tag }
            onChange={ this.onChange.bind(this, 'tags') }
        />
        */
    }

}

Filters.propTypes = {
    onChange: PropTypes.func,
    values: PropTypes.object
};

Filters.defaultProps = {
    onChange () {}, // no-op
    values: {}
};

export default Filters;
